import React from "react";
import { ConsoleRoleType, InterviewType } from "types/graphqlapi";
import { Status } from "types/status";

type ConsoleStateValueType = {
  email: string;
  userId: string;
  userName: string;
  screenName: string;
  selectedGroupId: string;
  selectedGroupName: string;
  selectedSubjectId: string;
  selectedSubjectStatus?: Status;
  selectedSubjectType?: InterviewType;
  role: ConsoleRoleType;
  isPersonalDataHandler: boolean;
  pageLevel: string;
  setEmail: (email: string) => void;
  setUserInfo: (
    id: string,
    name: string,
    role: ConsoleRoleType,
    isPDH: boolean
  ) => void;
  setScreenName: (name: string) => void;
  setGroup: (id: string, name: string) => void;
  setSubject: (subject?: ConsoleSubject) => void;
  setPageLevel: (pageLevel: string) => void;
  isOneGroupUser: boolean;
  subjectFilter: FilterProps;
  setSubjectFilter: React.Dispatch<React.SetStateAction<FilterProps>>;
};

type ConsoleSubject = {
  id: string;
  status?: Status;
  subjectType?: InterviewType;
};

type FilterProps = {
  status: "ALL" | Status;
  marked: "ALL" | "MARKED";
  // TODO できるならユニオンタイプの型をしっかり定義したい
  group: string;
  groupName: string;
  isSelectGroup: boolean;
};

export const PageLevel = {
  groupManagement: "0",
  subjectManagement: "R",
  userManagement: "G",
  interviewManagement: "3",
  fileAndScenarioAndMonitor: "4",
  mailHistoryManagement: "5",
};

const ConsoleStateContext = React.createContext<ConsoleStateValueType | null>(
  null
);

export const useConsoleState = (): ConsoleStateValueType => {
  const context = React.useContext(ConsoleStateContext);

  if (!context) {
    throw new Error("useConsoleState must be used within ConsoleStateProvider");
  }

  return context;
};

export const ConsoleStateProvider: React.FC = ({ children }) => {
  const [email, setEmail] = React.useState("");
  const [userId, setUserId] = React.useState("");
  const [userName, setUserName] = React.useState("");
  const [isPersonalDataHandler, setIsPersonalDataHandler] =
    React.useState<boolean>(false);
  const [screenName, setScreenName] = React.useState("");
  const [selectedGroupId, setSelectedGroupId] = React.useState("");
  const [selectedGroupName, setSelectedGroupName] = React.useState("");
  const [pageLevel, setPageLevel] = React.useState("");
  const [role, setRole] = React.useState<ConsoleRoleType>(
    ConsoleRoleType.GROUPUSER
  );
  const [selectedSubjectId, setSelectedSubjectId] = React.useState("");
  const [selectedSubjectStatus, setSelectedSubjectStatus] =
    React.useState<Status>();
  const [selectedSubjectType, setSelectedSubjectType] =
    React.useState<InterviewType>();
  const [subjectFilter, setSubjectFilter] = React.useState<FilterProps>({
    status: "ALL",
    marked: "ALL",
    group: "ALL",
    groupName: "",
    isSelectGroup: false,
  });

  const setUserInfo = (
    id: string,
    name: string,
    role: ConsoleRoleType,
    isPDH: boolean
  ) => {
    setUserId(id);
    setUserName(name);
    setRole(role);
    setIsPersonalDataHandler(isPDH);
    if (id == "") {
      setSelectedGroupId("");
      setSelectedGroupName("");
    }
  };

  const setGroup = (id: string, name: string) => {
    setSelectedGroupId(id);
    setSelectedGroupName(name);
  };

  const setSubject = (subject?: ConsoleSubject) => {
    if (subject) {
      setSelectedSubjectId(subject.id);
      setSelectedSubjectStatus(subject.status);
      setSelectedSubjectType(subject.subjectType);
    } else {
      setSelectedSubjectId("");
      setSelectedSubjectStatus(undefined);
      setSelectedSubjectType(undefined);
    }
  };

  const isOneGroupUser =
    role === ConsoleRoleType.GROUPMANAGER || role === ConsoleRoleType.GROUPUSER;

  const providerValue = {
    email,
    userId,
    userName,
    screenName,
    selectedGroupId,
    selectedGroupName,
    role,
    isPersonalDataHandler,
    selectedSubjectId,
    selectedSubjectStatus,
    selectedSubjectType,
    pageLevel,
    isOneGroupUser,
    subjectFilter,
    setEmail,
    setUserInfo,
    setScreenName,
    setGroup,
    setSubject,
    setPageLevel,
    setSubjectFilter,
  };

  return (
    <ConsoleStateContext.Provider value={providerValue}>
      {children}
    </ConsoleStateContext.Provider>
  );
};
